import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import '@babylonjs/core/Collisions/collisionCoordinator';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { CreateDisc } from '@babylonjs/core/Meshes/Builders/discBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { AudioSystem } from './audio/AudioSystem';
import { chooseInitialQuality, KICK_COOLDOWN, KICK_IMPACT_TIME, type QualityPreset } from './config/GameConfig';
import { createMonster, type BaseMonster } from './entities/Monster';
import { Player } from './entities/Player';
import { LevelBuilder } from './levels/LevelBuilder';
import { getLevel, LEVELS } from './levels/LevelDefinitions';
import type { LevelDefinition } from './levels/LevelTypes';
import { InputSystem } from './systems/InputSystem';
import { LeaderboardSystem } from './systems/LeaderboardSystem';
import { calculateScore, calculateStars, isLevelComplete, type RoundStats } from './systems/Progression';
import { SaveSystem } from './systems/SaveSystem';
import { GameUI } from './ui/GameUI';
import { angleFromMonsterFront, distance2D } from './utils/DirectionalMath';

type GameState = 'TITLE' | 'PLAYING' | 'PAUSED' | 'CAUGHT' | 'COMPLETE';

export interface GameSnapshot {
  state: GameState;
  level: number;
  totalMonsters: number;
  defeated: number;
  monsterStates: string[];
  debug: boolean;
  fps: number;
  playerPosition?: { x: number; z: number };
  playerModel?: 'rigged' | 'procedural';
}

export class Game {
  private readonly engine: Engine;
  private readonly input: InputSystem;
  private readonly saves: SaveSystem;
  private readonly audio: AudioSystem;
  private readonly leaderboard: LeaderboardSystem;
  private readonly ui: GameUI;
  private scene?: Scene;
  private camera?: ArcRotateCamera;
  private levelBuilder?: LevelBuilder;
  private player?: Player;
  private monsters: BaseMonster[] = [];
  private shadow?: ShadowGenerator;
  private state: GameState = 'TITLE';
  private levelId = 1;
  private defeated = 0;
  private elapsed = 0;
  private detected = 0;
  private closeEscapes = 0;
  private combo = 0;
  private bestCombo = 0;
  private comboTimer = 0;
  private previousPlayerPosition = Vector3.Zero();
  private playerVelocity = Vector3.Zero();
  private tutorialStep: 0 | 1 | 2 | 3 = 0;
  private tutorialTimer = 0;
  private levelGeneration = 0;
  private debugTimer = 0;
  private monsterSoundTimer = 0;
  private completionPending = false;
  private readonly debug = new URLSearchParams(location.search).get('debug') === 'true';
  private activeDefinition: LevelDefinition = getLevel(1);
  private testArena = false;
  private testApproach: 'front' | 'side' | 'rear' = 'front';

  constructor(private readonly canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: false, stencil: true, powerPreference: 'high-performance' }, true);
    this.saves = new SaveSystem(chooseInitialQuality());
    this.leaderboard = new LeaderboardSystem(this.saves.data.playerId);
    this.audio = new AudioSystem(this.saves.data.settings);
    this.input = new InputSystem();
    this.ui = new GameUI(uiRoot, {
      play: () => void this.begin(1),
      continueGame: () => void this.begin(this.saves.data.unlockedLevel),
      chooseLevel: (level) => void this.begin(level),
      resume: () => this.resume(),
      restart: () => void this.begin(this.levelId),
      nextLevel: () => void this.begin(Math.min(LEVELS.length, this.levelId + 1)),
      title: () => this.showTitle(),
      togglePause: () => this.togglePause(),
      leaderboard: () => void this.openLeaderboard(),
      playerNameChanged: (name) => {
        const savedName = this.saves.setPlayerName(name);
        this.submitLeaderboardScore();
        return savedName;
      },
      settingsChanged: (settings) => {
        this.saves.data.settings = settings; this.saves.save(); this.audio.applySettings(settings); this.applyQuality(settings.quality);
      },
    }, this.saves.data.settings);
    this.input.bindTouchControls(this.ui.joystick, this.ui.joystickKnob, this.ui.kickButton, this.ui.mobilePause);
    this.canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    window.addEventListener('resize', () => this.engine.resize());
    document.addEventListener('visibilitychange', () => { if (document.hidden && this.state === 'PLAYING') this.pause(); });
    this.showTitle();
    this.applyQuality(this.saves.data.settings.quality);
    this.engine.runRenderLoop(() => {
      const delta = Math.min(0.05, this.engine.getDeltaTime() / 1000);
      if (this.scene) {
        if (this.state === 'PLAYING') this.update(delta);
        else if (this.state === 'PAUSED' && this.input.readFrame().pausePressed) this.resume();
        if (this.scene.activeCamera) this.scene.render();
      }
    });
    document.querySelector('#boot-screen')?.classList.add('loaded');
  }

  async begin(level: number): Promise<void> {
    await this.audio.unlock(); this.audio.startMusic(); this.audio.play('transition');
    this.loadLevel(level);
  }

  async beginDirectionalTest(approach: 'front' | 'side' | 'rear'): Promise<void> {
    if (!this.debug) return;
    await this.audio.unlock();
    const positions = { front: { x: 0, z: 2 }, side: { x: 2, z: 0 }, rear: { x: 0, z: -2 } };
    this.testArena = true;
    this.testApproach = approach;
    this.loadDefinition({
      id: 1,
      name: `Directional Test · ${approach}`,
      subtitle: 'Developer combat validation arena',
      hint: 'Red front catches; green side and rear kick.',
      width: 20,
      depth: 20,
      playerSpawn: positions[approach],
      pieces: [],
      monsters: [{ type: 'stalker', x: 0, z: 0, yaw: 0 }],
      fogDensity: 0.005,
      timeOfDay: 'day',
    });
  }

  getSnapshot(): GameSnapshot {
    return {
      state: this.state, level: this.levelId, totalMonsters: this.monsters.length, defeated: this.defeated,
      monsterStates: this.monsters.map((monster) => monster.state), debug: this.debug, fps: Math.round(this.engine.getFps()),
      playerPosition: this.player ? { x: this.player.position.x, z: this.player.position.z } : undefined,
      playerModel: this.player?.visualMode,
    };
  }

  /** Debug/test hook, exposed only with ?debug=true. */
  debugTeleportPlayer(x: number, z: number): void {
    if (!this.debug || !this.player || !this.levelBuilder) return;
    this.player.position.set(x, this.levelBuilder.floorHeightAt(x, z) + 1.04, z);
  }

  private loadLevel(level: number): void {
    this.testArena = false;
    this.loadDefinition(getLevel(Math.max(1, Math.min(LEVELS.length, level))));
  }

  private loadDefinition(definition: LevelDefinition): void {
    this.levelGeneration += 1;
    this.scene?.dispose();
    this.activeDefinition = definition;
    this.levelId = definition.id;
    this.defeated = this.elapsed = this.detected = this.closeEscapes = this.combo = this.bestCombo = this.comboTimer = 0;
    this.completionPending = false;
    this.tutorialStep = this.levelId === 1 && !this.testArena ? 1 : 0; this.tutorialTimer = 0;
    this.state = 'PLAYING';

    const scene = new Scene(this.engine); this.scene = scene;
    scene.collisionsEnabled = true;
    scene.clearColor = this.skyColor(definition.timeOfDay);
    scene.fogMode = Scene.FOGMODE_EXP2; scene.fogDensity = definition.fogDensity; scene.fogColor = new Color3(scene.clearColor.r, scene.clearColor.g, scene.clearColor.b);
    scene.imageProcessingConfiguration.contrast = 1.18; scene.imageProcessingConfiguration.exposure = definition.timeOfDay === 'twilight' ? 1.05 : 1.18;
    scene.imageProcessingConfiguration.toneMappingEnabled = true;
    scene.imageProcessingConfiguration.vignetteEnabled = true; scene.imageProcessingConfiguration.vignetteWeight = 1.1;
    scene.imageProcessingConfiguration.vignetteColor = new Color4(0.03, 0.12, 0.12, 1);

    this.createSky(scene, definition.timeOfDay);
    const hemi = new HemisphericLight('soft-sky-light', new Vector3(0.2, 1, 0.15), scene);
    hemi.intensity = definition.timeOfDay === 'twilight' ? 0.72 : 0.9; hemi.diffuse = Color3.FromHexString('#C8E9E2'); hemi.groundColor = Color3.FromHexString('#72513C');
    const sun = new DirectionalLight('warm-sun', new Vector3(-0.55, -1, 0.35), scene);
    sun.position = new Vector3(20, 34, -18); sun.intensity = definition.timeOfDay === 'twilight' ? 1.6 : 2.25; sun.diffuse = Color3.FromHexString('#FFD29A');

    const particles = this.saves.data.settings.quality === 'LOW' ? 90 : this.saves.data.settings.quality === 'MEDIUM' ? 180 : 320;
    this.levelBuilder = new LevelBuilder(scene, definition, particles);
    this.player = new Player(scene, definition.playerSpawn);
    this.player.setDebugVisible(this.debug);
    this.previousPlayerPosition = this.player.position.clone();
    this.monsters = definition.monsters.map((spawn, index) => createMonster(scene, spawn, index, this.levelBuilder!.navigator, this.levelId));
    this.monsters.forEach((monster) => monster.setDebugVisible(this.debug));

    const target = this.player.position.add(new Vector3(0, 1.25, 0));
    this.camera = new ArcRotateCamera('adventure-camera', Math.PI / 2, 1.02, 11.2, target, scene);
    this.camera.minZ = 0.15; this.camera.lowerRadiusLimit = 5.3; this.camera.upperRadiusLimit = 13.5;
    this.camera.lowerBetaLimit = 0.66; this.camera.upperBetaLimit = 1.24; this.camera.angularSensibilityX = 2200; this.camera.angularSensibilityY = 2500;
    this.camera.inertia = 0.78; this.camera.wheelPrecision = 65; this.camera.panningSensibility = 0;
    this.camera.checkCollisions = true; this.camera.collisionRadius = new Vector3(0.48, 0.48, 0.48);
    this.camera.attachControl(this.canvas, true);
    scene.activeCamera = this.camera;

    const shadowSize = this.saves.data.settings.quality === 'HIGH' ? 1024 : 512;
    this.shadow = new ShadowGenerator(shadowSize, sun);
    this.shadow.useBlurExponentialShadowMap = true; this.shadow.blurKernel = this.saves.data.settings.quality === 'HIGH' ? 24 : 12; this.shadow.bias = 0.002;
    if (this.saves.data.settings.quality !== 'LOW') {
      this.shadow.addShadowCaster(this.player.collider, true);
      this.player.setShadowGenerator(this.shadow);
      this.monsters.forEach((monster) => this.shadow!.addShadowCaster(monster.collider, true));
    }
    this.ui.showHUD(); this.ui.showTutorial(this.tutorialStep);
  }

  private update(delta: number): void {
    if (!this.player || !this.camera || !this.levelBuilder) return;
    const input = this.input.readFrame();
    if (input.pausePressed) { this.pause(); return; }
    this.camera.alpha -= input.cameraX; this.camera.beta = Math.max(this.camera.lowerBetaLimit ?? 0.66, Math.min(this.camera.upperBetaLimit ?? 1.24, this.camera.beta + input.cameraY));
    this.elapsed += delta; this.tutorialTimer += delta; this.comboTimer -= delta;
    this.monsterSoundTimer -= delta;
    if (this.comboTimer <= 0) this.combo = 0;

    this.previousPlayerPosition.copyFrom(this.player.position);
    this.player.update(delta, input, this.camera.position, (x, z) => this.levelBuilder!.floorHeightAt(x, z), (sprint) => this.audio.play('footstep', sprint ? 1 : 0.7));
    const bounds = this.activeDefinition;
    this.player.position.x = Math.max(-bounds.width / 2 + 1, Math.min(bounds.width / 2 - 1, this.player.position.x));
    this.player.position.z = Math.max(-bounds.depth / 2 + 1, Math.min(bounds.depth / 2 - 1, this.player.position.z));
    this.playerVelocity = this.player.position.subtract(this.previousPlayerPosition).scale(1 / Math.max(0.001, delta));
    const desiredTarget = this.player.position.add(new Vector3(0, 1.35, 0));
    this.camera.setTarget(Vector3.Lerp(this.camera.target, desiredTarget, 1 - Math.exp(-delta * 8)));

    if (!this.testArena || this.testApproach === 'front') this.monsters.forEach((monster) => monster.update(delta, {
      playerPosition: this.player!.position,
      playerVelocity: this.playerVelocity,
      playerSprinting: input.sprint && this.playerVelocity.lengthSquared() > 3,
      nearbyMonsters: this.monsters,
      floorHeight: (x, z) => this.levelBuilder!.floorHeightAt(x, z),
      paused: this.state !== 'PLAYING',
      onDetected: () => {
        this.detected += 1; this.audio.play('alert');
      },
      onAttackStart: () => this.audio.play('snap'),
      onCaught: () => this.caught(),
      onCloseEscape: () => { this.closeEscapes += 1; this.player?.stumble(); },
    }));
    if (this.monsterSoundTimer <= 0 && this.monsters.some((monster) => !monster.defeated
      && (monster.state === 'CHASE' || monster.state === 'PATROL') && distance2D(monster.position, this.player!.position) < 10)) {
      this.audio.play('monsterMove', 0.75); this.monsterSoundTimer = 1.05 + Math.random() * 0.8;
    }

    const kickTarget = this.monsters
      .filter((monster) => monster.isKickableFrom(this.player!.position))
      .sort((a, b) => distance2D(a.position, this.player!.position) - distance2D(b.position, this.player!.position))[0];
    this.ui.showKickPrompt(Boolean(kickTarget));
    if (kickTarget && this.levelId === 1 && this.tutorialStep < 3) { this.tutorialStep = 3; this.ui.showTutorial(3); }
    if (input.kickPressed && !this.completionPending && this.player.startKick(kickTarget?.position)) {
      if (kickTarget) {
        const generation = this.levelGeneration;
        window.setTimeout(() => {
          if (generation === this.levelGeneration && this.state === 'PLAYING' && !kickTarget.defeated) this.kick(kickTarget);
        }, KICK_IMPACT_TIME * 1000);
      }
      else this.audio.play('ui');
    }

    const danger = this.monsters.some((monster) => !monster.defeated && distance2D(monster.position, this.player!.position) < 5
      && angleFromMonsterFront(monster.position, monster.yaw, this.player!.position) < monster.attackAngle * 0.58);
    this.ui.showDanger(danger);

    if (this.levelId === 1 && this.tutorialStep === 1
      && ((this.detected > 0 && this.tutorialTimer > 3.2) || this.tutorialTimer > 6)) {
      this.tutorialStep = 2; this.ui.showTutorial(2);
    }
    this.ui.updateHUD({
      level: this.levelId, levelName: this.activeDefinition.name, defeated: this.defeated, total: this.monsters.length,
      elapsed: this.elapsed, detected: this.detected, combo: this.combo,
    });
    this.updateDebug(delta);
  }

  private kick(monster: BaseMonster): void {
    if (!this.player) return;
    monster.defeat(this.player.position); this.defeated += 1;
    this.combo = this.comboTimer > 0 ? this.combo + 1 : 1; this.comboTimer = 4.2; this.bestCombo = Math.max(this.bestCombo, this.combo);
    this.audio.play('kick'); window.setTimeout(() => this.audio.play('defeat'), 90);
    if (this.levelId === 1 && this.tutorialStep > 0) { this.tutorialStep = 0; window.setTimeout(() => this.ui.showTutorial(0), 950); }
    if (isLevelComplete(this.defeated, this.monsters.length)) {
      const generation = this.levelGeneration;
      this.completionPending = true;
      const kickRecoveryDelay = Math.max(0, (KICK_COOLDOWN - KICK_IMPACT_TIME) * 1000);
      if (this.testArena) {
        window.setTimeout(() => { if (generation === this.levelGeneration) this.state = 'COMPLETE'; }, Math.max(1400, kickRecoveryDelay + 80));
        return;
      }
      window.setTimeout(() => { if (generation === this.levelGeneration) this.player?.celebrate(); }, kickRecoveryDelay);
      window.setTimeout(() => { if (generation === this.levelGeneration) this.completeLevel(); }, 1350);
    }
  }

  private caught(): void {
    if (this.state !== 'PLAYING') return;
    this.state = 'CAUGHT'; this.audio.play('caught'); this.ui.playCapture();
    const generation = this.levelGeneration;
    window.setTimeout(() => { if (generation === this.levelGeneration) this.ui.showGameOver(this.levelId); }, 720);
  }

  private completeLevel(): void {
    this.state = 'COMPLETE'; this.completionPending = false;
    const stats: RoundStats = {
      elapsedSeconds: this.elapsed, detected: this.detected, closeEscapes: this.closeEscapes,
      bestCombo: this.bestCombo, defeated: this.defeated,
    };
    const stars = calculateStars(this.levelId, stats); const score = calculateScore(this.levelId, stats);
    this.saves.completeLevel(this.levelId, { stars, bestTime: this.elapsed, bestScore: score });
    this.submitLeaderboardScore();
    const final = this.levelId === LEVELS.length;
    const displayedScore = final
      ? Object.values(this.saves.data.completedLevels).reduce((total, record) => total + record.bestScore, 0)
      : score;
    this.audio.play('victory'); this.ui.showComplete(this.levelId, stats, stars, displayedScore, final);
  }

  private togglePause(): void { if (this.state === 'PLAYING') this.pause(); else if (this.state === 'PAUSED') this.resume(); }
  private pause(): void { if (this.state !== 'PLAYING') return; this.state = 'PAUSED'; this.ui.showPause(); }
  private resume(): void { if (this.state !== 'PAUSED') return; this.state = 'PLAYING'; this.ui.showHUD(); }

  private showTitle(): void {
    this.levelGeneration += 1; this.state = 'TITLE'; this.scene?.dispose(); this.scene = undefined; this.camera = undefined;
    this.monsters = []; this.player = undefined; this.levelBuilder = undefined; this.ui.showTitle(LEVELS, this.saves.data);
  }

  private async openLeaderboard(): Promise<void> {
    this.ui.showLeaderboardLoading();
    const result = await this.leaderboard.list();
    this.ui.showLeaderboard(result, this.saves.data.playerId);
  }

  private submitLeaderboardScore(): void {
    const records = Object.entries(this.saves.data.completedLevels);
    if (!records.length) return;
    const score = records.reduce((total, [, record]) => total + record.bestScore, 0);
    const level = Math.max(...records.map(([id]) => Number(id)));
    const stars = records.reduce((total, [, record]) => total + record.stars, 0);
    void this.leaderboard.submit(this.saves.data.playerName, score, level, stars);
  }

  private updateDebug(delta: number): void {
    if (!this.debug || !this.player) return;
    this.debugTimer -= delta; if (this.debugTimer > 0) return; this.debugTimer = 0.18;
    const states = this.monsters.reduce<Record<string, number>>((acc, monster) => { acc[monster.state] = (acc[monster.state] ?? 0) + 1; return acc; }, {});
    this.ui.showDebug(true, `<b>DEBUG · ${Math.round(this.engine.getFps())} FPS</b><br>Player ${this.player.position.x.toFixed(1)}, ${this.player.position.z.toFixed(1)}<br>
      Forward: yellow · Mouth: red · Kick: green<br>Detection: gold · Paths: cyan<br>${Object.entries(states).map(([state, count]) => `${state}: ${count}`).join(' · ')}`);
  }

  private applyQuality(quality: QualityPreset): void {
    const scale = quality === 'LOW' ? 1.55 : quality === 'MEDIUM' ? 1.22 : 1;
    this.engine.setHardwareScalingLevel(scale);
  }

  private createSky(scene: Scene, time: 'sunrise' | 'day' | 'sunset' | 'twilight'): void {
    const sky = CreateSphere('painted-sky', { diameter: 190, segments: 16 }, scene);
    const mat = new StandardMaterial('sky-material', scene);
    const colors = { sunrise: '#72A6B2', day: '#67AFC0', sunset: '#A56F73', twilight: '#405A78' };
    mat.emissiveColor = Color3.FromHexString(colors[time]); mat.diffuseColor = mat.emissiveColor; mat.backFaceCulling = false; mat.disableLighting = true;
    sky.material = mat; sky.isPickable = false;
    const sun = CreateDisc('sun-disc', { radius: 5, tessellation: 32 }, scene);
    sun.position.set(-45, 32, 55); sun.rotation.y = Math.PI; const sunMat = new StandardMaterial('sun-disc-material', scene);
    sunMat.emissiveColor = Color3.FromHexString('#FFD98A'); sunMat.disableLighting = true; sun.material = sunMat; sun.isPickable = false;
  }

  private skyColor(time: 'sunrise' | 'day' | 'sunset' | 'twilight'): Color4 {
    const colors = { sunrise: '#84B7B8', day: '#77B7C2', sunset: '#B07B78', twilight: '#52637B' };
    const color = Color3.FromHexString(colors[time]); return new Color4(color.r, color.g, color.b, 1);
  }
}
